import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import EcoFiltersCard from "../../../components/eco-ui/EcoFiltersCard";
import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import { HOMEPAGE } from "../../home/routes";
import { OPERACOES_PAGE } from "../../operacoes/routes";
import { STORE_EVALUATIONS_ADMIN_PAGE } from "../routes";
import { getMassBalanceStoresApi } from "../../estoque/api/massBalanceApi";
import type { StoreShort } from "../../estoque/api/massBalanceApi";
import { getUsersPaginatedApi } from "../../users/usersApi";
import type { UserListItem } from "../../users/usersApi";
import { listEvaluationFormsApi } from "../../evaluations/evaluationFormsApi";
import {
  getQuestionDeficienciesApi,
  getStoreRankingApi,
  getScoreTrendApi,
  getStoreQuestionHeatmapApi,
  getScoreEvolutionApi
} from "../storeEvaluationsApi";

import { useGoBack } from "../../../hooks/useGoBack";

import "./StoreQualityDashboardPage.css";

// Available UFs
const UF_LIST = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];

/** Quantos itens o ranking e as deficiências mostram antes do "Ver todas". */
const PREVIEW_ROWS = 5;

type PeriodPreset = "30d" | "3m" | "6m" | "custom";

const PERIOD_OPTIONS: { value: PeriodPreset; label: string }[] = [
  { value: "30d", label: "30 dias" },
  { value: "3m", label: "3 meses" },
  { value: "6m", label: "6 meses" },
  { value: "custom", label: "Personalizado" },
];

const DEFAULT_PERIOD = "6m" as const;

const toYmd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Intervalo de datas do atalho de período, terminando hoje. */
const presetRange = (preset: Exclude<PeriodPreset, "custom">) => {
  const end = new Date();
  const start = new Date(end);
  if (preset === "30d") start.setDate(start.getDate() - 30);
  else start.setMonth(start.getMonth() - (preset === "3m" ? 3 : 6));
  return { from: toYmd(start), to: toYmd(end) };
};

/** Faixas de nota — as mesmas da distribuição que a tela já usava. */
type Band = "good" | "warn" | "bad";
const scoreBand = (score01: number): Band => (score01 >= 0.8 ? "good" : score01 >= 0.5 ? "warn" : "bad");
/** Faixas do % de "Não" — as mesmas do mapa de calor que a tela já usava. */
const noRateBand = (rate: number): Band => (rate > 0.4 ? "bad" : rate > 0.1 ? "warn" : "good");

const pct = (v: number) => `${(Number(v) * 100).toFixed(0)}%`;

const monthLabel = (period: string, style: "short" | "long") => {
  const [y, m] = period.split("-").map(Number);
  if (!y || !m) return period;
  const text = new Intl.DateTimeFormat("pt-BR", { month: style }).format(new Date(y, m - 1, 1));
  const clean = text.replace(".", "");
  return clean.charAt(0).toUpperCase() + clean.slice(1);
};

const Bar: React.FC<{ value01: number; band: Band }> = ({ value01, band }) => (
  <span className="qDash__bar" aria-hidden="true">
    <span
      className={`qDash__barFill qDash__barFill--${band}`}
      style={{ width: `${Math.max(0, Math.min(1, value01)) * 100}%` }}
    />
  </span>
);

export function StoreQualityDashboardPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(OPERACOES_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  // Filter options
  const [stores, setStores] = useState<StoreShort[]>([]);
  const [supervisors, setSupervisors] = useState<UserListItem[]>([]);
  const [forms, setForms] = useState<any[]>([]);

  // Filter values
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [selectedUf, setSelectedUf] = useState("");
  const [selectedSupervisorId, setSelectedSupervisorId] = useState("");
  const [selectedFormId, setSelectedFormId] = useState("");
  const [period, setPeriod] = useState<PeriodPreset>(DEFAULT_PERIOD);
  const [startDate, setStartDate] = useState(() => presetRange(DEFAULT_PERIOD).from);
  const [endDate, setEndDate] = useState(() => presetRange(DEFAULT_PERIOD).to);

  // Report results
  const [trends, setTrends] = useState<any[]>([]);
  const [rankings, setRankings] = useState<any[]>([]);
  const [deficiencies, setDeficiencies] = useState<any[]>([]);
  const [heatmap, setHeatmap] = useState<any>({ questions: [], stores: [] });
  const [evolution, setEvolution] = useState<any | null>(null);

  const [loading, setLoading] = useState(false);
  const [showAllRanking, setShowAllRanking] = useState(false);
  const [showAllDeficiencies, setShowAllDeficiencies] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
    }
  }, [token, navigate]);

  // Load filter options on mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      if (!token) return;
      try {
        const [storesData, usersData, formsData] = await Promise.all([
          getMassBalanceStoresApi(token),
          getUsersPaginatedApi(1, 200),
          listEvaluationFormsApi({ category: "LOJA" })
        ]);
        setStores(storesData || []);
        setSupervisors((usersData.items || []).filter(u => u.userType === "SUPERVISOR" || u.userType === "SUPERVISOR_DE_CAMPO"));
        setForms(formsData || []);
      } catch (err) {
        console.error("Erro ao buscar opções de filtros", err);
      }
    };
    loadFilterOptions();
  }, [token]);

  // Fetch reports based on active filters
  const handleApplyFilters = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const params = {
        storeId: selectedStoreId || undefined,
        estado: selectedUf || undefined,
        answeredById: selectedSupervisorId || undefined,
        formId: selectedFormId || undefined,
        referenceFrom: startDate || undefined,
        referenceTo: endDate || undefined
      };

      const [trendsData, rankingsData, deficienciesData, heatmapData] = await Promise.all([
        getScoreTrendApi(token, params).catch(() => []),
        getStoreRankingApi(token, params).catch(() => []),
        getQuestionDeficienciesApi(token, params).catch(() => []),
        getStoreQuestionHeatmapApi(token, params).catch(() => ({ questions: [], stores: [] }))
      ]);

      setTrends(trendsData || []);
      setRankings(rankingsData || []);
      setDeficiencies(deficienciesData || []);
      setHeatmap(heatmapData || { questions: [], stores: [] });

      // Load evolution if a specific store is selected
      if (selectedStoreId) {
        const evolutionData = await getScoreEvolutionApi(token, {
          storeId: selectedStoreId,
          referenceFrom: startDate || undefined,
          referenceTo: endDate || undefined
        }).catch(() => null);
        setEvolution(evolutionData);
      } else {
        setEvolution(null);
      }
    } catch (err: any) {
      toast.error("Erro ao carregar dados do relatório.");
    } finally {
      setLoading(false);
    }
  }, [token, selectedStoreId, selectedUf, selectedSupervisorId, selectedFormId, startDate, endDate]);

  // Load automatically on mount / filter changes
  useEffect(() => {
    handleApplyFilters();
  }, [handleApplyFilters]);

  const handlePeriodChange = (next: PeriodPreset) => {
    setPeriod(next);
    // "Personalizado" mantém as datas atuais como ponto de partida para edição.
    if (next !== "custom") {
      const range = presetRange(next);
      setStartDate(range.from);
      setEndDate(range.to);
    }
  };

  const handleClearFilters = () => {
    setSelectedStoreId("");
    setSelectedUf("");
    setSelectedSupervisorId("");
    setSelectedFormId("");
    handlePeriodChange(DEFAULT_PERIOD);
  };

  const activeFilterCount =
    [selectedStoreId, selectedUf, selectedSupervisorId, selectedFormId].filter(Boolean).length +
    (period === DEFAULT_PERIOD ? 0 : 1);

  // Visão Geral calculations
  const distribution = useMemo(() => {
    if (!rankings || rankings.length === 0) return { good: 0, regular: 0, bad: 0 };
    let good = 0;
    let regular = 0;
    let bad = 0;
    rankings.forEach((r) => {
      const score = Number(r.mediaScore01) * 100;
      if (score >= 80) good++;
      else if (score >= 50) regular++;
      else bad++;
    });
    return { good, regular, bad };
  }, [rankings]);

  const topStores = useMemo(() => {
    return [...rankings].sort((a, b) => b.mediaScore01 - a.mediaScore01).slice(0, 3);
  }, [rankings]);

  const bottomStores = useMemo(() => {
    return [...rankings].sort((a, b) => a.mediaScore01 - b.mediaScore01).slice(0, 3);
  }, [rankings]);

  // Indicadores do topo, todos derivados do que a tela já carrega.
  const kpis = useMemo(() => {
    const totalAudits = trends.reduce((s, t) => s + Number(t.totalAvaliacoes || 0), 0);
    const weighted = trends.reduce(
      (s, t) => s + Number(t.mediaScore01 || 0) * Number(t.totalAvaliacoes || 0),
      0,
    );
    const average = totalAudits > 0 ? weighted / totalAudits : null;

    const last = trends[trends.length - 1];
    const prev = trends[trends.length - 2];
    const deltaPoints =
      last && prev ? Math.round(Number(last.mediaScore01) * 100) - Math.round(Number(prev.mediaScore01) * 100) : null;

    const criticalNames = rankings
      .filter((r) => scoreBand(Number(r.mediaScore01)) === "bad")
      .map((r) => r.nomeFantasia as string);

    const first = trends[0];
    const span =
      first && last
        ? first.period === last.period
          ? monthLabel(first.period, "long").toLowerCase()
          : `${monthLabel(first.period, "long").toLowerCase()} a ${monthLabel(last.period, "long").toLowerCase()}`
        : "";

    return { totalAudits, average, deltaPoints, prevPeriod: prev?.period as string | undefined, criticalNames, span };
  }, [trends, rankings]);

  const rankingRows = showAllRanking ? rankings : rankings.slice(0, PREVIEW_ROWS);
  const deficiencyRows = showAllDeficiencies ? deficiencies : deficiencies.slice(0, PREVIEW_ROWS);
  const selectedStoreName = stores.find((s) => s.id === selectedStoreId)?.nomeFantasia;
  const lastTwo = new Set(trends.slice(-2).map((t) => t.period));

  return (
    <div className="qDash">
      <main className="qDash__main">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate(HOMEPAGE()) },
            { label: "Operações", onClick: () => navigate(OPERACOES_PAGE()) },
            { label: "Qualidade de loja" },
          ]}
          title="Qualidade de loja"
          subtitle="Notas de conformidade das auditorias finalizadas, por loja e por pergunta."
          onBack={goBack}
          backLabel="Voltar para Operações"
          actions={
            <button type="button" className="ecoBtn" onClick={() => navigate(STORE_EVALUATIONS_ADMIN_PAGE())}>
              <EcoIcon name="checkSquare" size={18} />
              Ver avaliações
            </button>
          }
        />

        {/* Filtros */}
        <EcoFiltersCard
          activeCount={activeFilterCount}
          summary={`${PERIOD_OPTIONS.find((p) => p.value === period)?.label} · ${selectedStoreName ?? "Todas as lojas"}`}
        >
            <div className="ecoFilters__grid">
              <div className="ecoField">
                <label htmlFor="q-loja">Loja</label>
                <select id="q-loja" className="ecoSelect ecoSelect--sm" value={selectedStoreId} onChange={(e) => setSelectedStoreId(e.target.value)}>
                  <option value="">Todas as lojas</option>
                  {stores.map((s) => (
                    <option key={s.id} value={s.id}>{s.nomeFantasia}</option>
                  ))}
                </select>
              </div>

              <div className="ecoField">
                <label htmlFor="q-uf">UF</label>
                <select id="q-uf" className="ecoSelect ecoSelect--sm" value={selectedUf} onChange={(e) => setSelectedUf(e.target.value)}>
                  <option value="">Todas</option>
                  {UF_LIST.map((uf) => (
                    <option key={uf} value={uf}>{uf}</option>
                  ))}
                </select>
              </div>

              <div className="ecoField">
                <label htmlFor="q-sup">Supervisor</label>
                <select id="q-sup" className="ecoSelect ecoSelect--sm" value={selectedSupervisorId} onChange={(e) => setSelectedSupervisorId(e.target.value)}>
                  <option value="">Todos</option>
                  {supervisors.map((s) => (
                    <option key={s.id} value={s.id}>{s.nomeCompleto}</option>
                  ))}
                </select>
              </div>

              <div className="ecoField">
                <label htmlFor="q-form">Formulário</label>
                <select id="q-form" className="ecoSelect ecoSelect--sm" value={selectedFormId} onChange={(e) => setSelectedFormId(e.target.value)}>
                  <option value="">Todos da categoria Loja</option>
                  {forms.map((f) => (
                    <option key={f.id} value={f.id}>{f.title || f.formType?.name || `Formulário - ${f.id}`}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="ecoFilters__row">
              <div className="ecoField">
                <span className="ecoLabel">Período</span>
                <EcoSegmented label="Período" options={PERIOD_OPTIONS} value={period} onChange={handlePeriodChange} className="qDash__period" />
              </div>

              {period === "custom" ? (
                <div className="qDash__dates">
                  <div className="ecoField">
                    <label htmlFor="q-de">De</label>
                    <input id="q-de" type="date" className="ecoInput ecoInput--sm" value={startDate} max={endDate || undefined} onChange={(e) => setStartDate(e.target.value)} />
                  </div>
                  <div className="ecoField">
                    <label htmlFor="q-ate">Até</label>
                    <input id="q-ate" type="date" className="ecoInput ecoInput--sm" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} />
                  </div>
                </div>
              ) : null}

              <button type="button" className="ecoBtn ecoFilters__clear" onClick={handleClearFilters}>
                Limpar
              </button>
            </div>
        </EcoFiltersCard>

        {loading ? (
          <p className="qDash__loading" role="status">Carregando relatórios...</p>
        ) : (
          <>
            {/* Indicadores */}
            <section className="qDash__kpis" aria-label="Indicadores">
              <div className="ecoCard qDash__kpi">
                <span className="qDash__kpiLabel">Nota média no período</span>
                <span className="qDash__kpiValue">{kpis.average === null ? "—" : pct(kpis.average)}</span>
                <span className="qDash__kpiNote">
                  {kpis.deltaPoints === null || !kpis.prevPeriod ? (
                    "média das auditorias no filtro"
                  ) : kpis.deltaPoints === 0 ? (
                    `igual a ${monthLabel(kpis.prevPeriod, "long").toLowerCase()}`
                  ) : (
                    <>
                      <EcoIcon name={kpis.deltaPoints < 0 ? "arrowDown" : "arrowUp"} size={14} strokeWidth={2.4} />
                      {Math.abs(kpis.deltaPoints)} {Math.abs(kpis.deltaPoints) === 1 ? "ponto" : "pontos"}{" "}
                      {kpis.deltaPoints < 0 ? "abaixo" : "acima"} de {monthLabel(kpis.prevPeriod, "long").toLowerCase()}
                    </>
                  )}
                </span>
              </div>

              <div className="ecoCard qDash__kpi">
                <span className="qDash__kpiLabel">Auditorias finalizadas</span>
                <span className="qDash__kpiValue">{kpis.totalAudits}</span>
                <span className="qDash__kpiNote">
                  em {rankings.length} {rankings.length === 1 ? "loja" : "lojas"}
                  {kpis.span ? `, ${kpis.span}` : ""}
                </span>
              </div>

              <div className="ecoCard qDash__kpi">
                <span className="qDash__kpiLabel">Lojas conformes</span>
                <span className="qDash__kpiValue">
                  {distribution.good} <span className="qDash__kpiOf">de {rankings.length}</span>
                </span>
                <span className="qDash__kpiNote">nota média igual ou acima de 80%</span>
              </div>

              <div className="ecoCard qDash__kpi">
                <span className="qDash__kpiLabel">Lojas críticas</span>
                <span className="qDash__kpiValue">{distribution.bad}</span>
                <span className={`qDash__kpiNote${distribution.bad ? " qDash__kpiNote--bad" : ""}`}>
                  {distribution.bad ? <EcoIcon name="alertTriangle" size={14} strokeWidth={2.4} /> : null}
                  {distribution.bad && kpis.criticalNames.length <= 3
                    ? `nota abaixo de 50%: ${kpis.criticalNames.join(" e ")}`
                    : "nota abaixo de 50%"}
                </span>
              </div>
            </section>

            {/* Tendência + distribuição */}
            <div className="qDash__row">
              <section className="ecoCard qDash__panel" aria-labelledby="q-trend-title">
                <div>
                  <h2 id="q-trend-title" className="ecoCard__title">Tendência da nota média</h2>
                  <p className="ecoCard__subtitle">Média mensal das auditorias finalizadas no filtro.</p>
                </div>

                {trends.length === 0 ? (
                  <p className="ecoEmpty">Sem dados de tendência para o período selecionado.</p>
                ) : (
                  <div className="qChart">
                    <div className="qChart__axis" aria-hidden="true">
                      <span>100%</span>
                      <span>50%</span>
                      <span>0</span>
                    </div>
                    <div className="qChart__plot">
                      <div className="qChart__grid" aria-hidden="true">
                        <span />
                        <span />
                        <span />
                      </div>
                      <ul
                        className="qChart__bars"
                        style={{ gridTemplateColumns: `repeat(${trends.length}, minmax(0, 1fr))` }}
                        aria-label="Nota média por mês"
                      >
                        {trends.map((t) => {
                          const label = `${monthLabel(t.period, "long")} de ${t.period.slice(0, 4)}: ${pct(t.mediaScore01)}, ${t.totalAvaliacoes} ${Number(t.totalAvaliacoes) === 1 ? "auditoria" : "auditorias"}`;
                          return (
                            <li key={t.period} className="qChart__col" tabIndex={0} aria-label={label}>
                              {lastTwo.has(t.period) ? (
                                <span className="qChart__value" aria-hidden="true">{pct(t.mediaScore01)}</span>
                              ) : null}
                              <span className="qChart__bar" style={{ height: `${Math.max(0, Math.min(1, Number(t.mediaScore01))) * 100}%` }} />
                              <span className="qChart__tip" role="tooltip">{label}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                    <div aria-hidden="true" />
                    <div
                      className="qChart__labels"
                      style={{ gridTemplateColumns: `repeat(${trends.length}, minmax(0, 1fr))` }}
                      aria-hidden="true"
                    >
                      {trends.map((t) => (
                        <span key={t.period}>{monthLabel(t.period, "short")}</span>
                      ))}
                    </div>
                  </div>
                )}
              </section>

              <section className="ecoCard qDash__panel" aria-labelledby="q-dist-title">
                <div>
                  <h2 id="q-dist-title" className="ecoCard__title">Distribuição das lojas por nota</h2>
                  <p className="ecoCard__subtitle">
                    {rankings.length} {rankings.length === 1 ? "loja avaliada" : "lojas avaliadas"} no filtro.
                  </p>
                </div>

                {rankings.length === 0 ? (
                  <p className="ecoEmpty">Sem lojas avaliadas no filtro selecionado.</p>
                ) : (
                  <>
                    <div className="qDist">
                      {([
                        ["good", "Conforme, 80% ou mais", distribution.good],
                        ["warn", "Regular, 50% a 79%", distribution.regular],
                        ["bad", "Crítico, abaixo de 50%", distribution.bad],
                      ] as [Band, string, number][]).map(([band, label, count]) => (
                        <div key={band} className="qDist__item">
                          <div className="qDist__line">
                            <span className="qDist__label">
                              <span className={`qDot qDot--${band}`} aria-hidden="true" />
                              {label}
                            </span>
                            <strong>{count} {count === 1 ? "loja" : "lojas"}</strong>
                          </div>
                          <Bar value01={count / rankings.length} band={band} />
                        </div>
                      ))}
                    </div>

                    <div className="qDist__extremes">
                      <div className="qDist__list">
                        <span className="qDist__listTitle">Melhores notas</span>
                        {topStores.map((s) => (
                          <div key={s.storeId} className="qDist__listRow">
                            <span>{s.nomeFantasia}</span>
                            <strong>{pct(s.mediaScore01)}</strong>
                          </div>
                        ))}
                      </div>
                      <div className="qDist__list">
                        <span className="qDist__listTitle">Piores notas</span>
                        {bottomStores.map((s) => (
                          <div key={s.storeId} className="qDist__listRow">
                            <span>{s.nomeFantasia}</span>
                            <strong>{pct(s.mediaScore01)}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </section>
            </div>

            {/* Deficiências + ranking */}
            <div className="qDash__row">
              <section className="ecoCard qDash__panel qDash__panel--flush" aria-labelledby="q-def-title">
                <div className="qDash__panelHead">
                  <div>
                    <h2 id="q-def-title" className="ecoCard__title">Maiores deficiências</h2>
                    <p className="ecoCard__subtitle">Perguntas com mais respostas "Não". Pontos de melhoria prioritários.</p>
                  </div>
                  {deficiencies.length > PREVIEW_ROWS ? (
                    <button type="button" className="ecoLinkBtn" onClick={() => setShowAllDeficiencies((v) => !v)}>
                      {showAllDeficiencies ? "Ver menos" : `Ver as ${deficiencies.length}`}
                    </button>
                  ) : null}
                </div>

                {deficiencies.length === 0 ? (
                  <div className="qDash__panelPad">
                    <p className="ecoEmpty">Nenhum dado encontrado para os filtros selecionados.</p>
                  </div>
                ) : (
                  <div className="ecoTableWrap">
                    <table className="ecoTable qDash__table">
                      <thead>
                        <tr>
                          <th scope="col">Pergunta</th>
                          <th scope="col" className="ecoTable__num">"Não"</th>
                          <th scope="col">% de "Não"</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deficiencyRows.map((d) => (
                          <tr key={d.questionId}>
                            <td className="qDash__prompt" title={d.prompt}>{d.prompt}</td>
                            <td className="ecoTable__num ecoTable__muted">{d.totalNao} de {d.totalRespostas}</td>
                            <td>
                              <span className="qDash__barCell">
                                <Bar value01={Number(d.percentualNao)} band={noRateBand(Number(d.percentualNao))} />
                                <strong>{pct(d.percentualNao)}</strong>
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section className="ecoCard qDash__panel qDash__panel--flush" aria-labelledby="q-rank-title">
                <div className="qDash__panelHead">
                  <div>
                    <h2 id="q-rank-title" className="ecoCard__title">Ranking de lojas</h2>
                    <p className="ecoCard__subtitle">Da pior para a melhor nota média.</p>
                  </div>
                  {rankings.length > PREVIEW_ROWS ? (
                    <button type="button" className="ecoLinkBtn" onClick={() => setShowAllRanking((v) => !v)}>
                      {showAllRanking ? "Ver menos" : `Ver as ${rankings.length}`}
                    </button>
                  ) : null}
                </div>

                {rankings.length === 0 ? (
                  <div className="qDash__panelPad">
                    <p className="ecoEmpty">Nenhum dado encontrado para os filtros selecionados.</p>
                  </div>
                ) : (
                  <div className="ecoTableWrap">
                    <table className="ecoTable qDash__table">
                      <thead>
                        <tr>
                          <th scope="col">Loja</th>
                          <th scope="col">UF</th>
                          <th scope="col" className="ecoTable__num">Auditorias</th>
                          <th scope="col">Nota média</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rankingRows.map((r) => (
                          <tr key={r.storeId}>
                            <td className="qDash__strong">{r.nomeFantasia}</td>
                            <td className="ecoTable__muted">{r.estado || "—"}</td>
                            <td className="ecoTable__num ecoTable__muted">{r.totalAvaliacoes}</td>
                            <td>
                              <span className="qDash__barCell">
                                <Bar value01={Number(r.mediaScore01)} band={scoreBand(Number(r.mediaScore01))} />
                                <strong>{pct(r.mediaScore01)}</strong>
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>

            {/* Mapa de calor */}
            <section className="ecoCard qDash__panel qDash__panel--flush qDash__heat" aria-labelledby="q-heat-title">
              <div className="qDash__panelHead">
                <div>
                  <h2 id="q-heat-title" className="ecoCard__title">Mapa de calor, loja por pergunta</h2>
                  <p className="ecoCard__subtitle">Percentual de respostas "Não" de cada loja em cada pergunta.</p>
                </div>
                <div className="qHeat__legend">
                  <span><span className="qHeat__swatch qHeat__swatch--good" aria-hidden="true" />até 10%</span>
                  <span><span className="qHeat__swatch qHeat__swatch--warn" aria-hidden="true" />11% a 40%</span>
                  <span><span className="qHeat__swatch qHeat__swatch--bad" aria-hidden="true" />acima de 40%</span>
                </div>
              </div>

              {!heatmap.stores || heatmap.stores.length === 0 ? (
                <div className="qDash__panelPad">
                  <p className="ecoEmpty">Sem dados suficientes para montar o mapa de calor.</p>
                </div>
              ) : (
                <div className="ecoTableWrap">
                  <table className="ecoTable qHeat">
                    <thead>
                      <tr>
                        <th scope="col" className="qHeat__sticky">Loja</th>
                        {heatmap.questions?.map((q: any) => (
                          <th key={q.questionId} scope="col" className="qHeat__q" title={q.prompt}>
                            Q{q.order}: {q.prompt.length > 25 ? q.prompt.substring(0, 25) + "..." : q.prompt}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {heatmap.stores.map((store: any) => (
                        <tr key={store.storeId}>
                          <th scope="row" className="qHeat__sticky qHeat__store">
                            {store.nomeFantasia}
                            {store.estado ? <span className="qHeat__uf"> {store.estado}</span> : null}
                          </th>
                          {store.cells?.map((cell: any, idx: number) => (
                            <td key={idx} className={`qHeat__cell qHeat__cell--${noRateBand(cell.percentualNao)}`}>
                              {pct(cell.percentualNao)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
            <p className="qDash__heatNote">O mapa de calor por pergunta fica disponível na versão para computador.</p>

            {/* Evolução da loja */}
            <section className="ecoCard qDash__panel" aria-labelledby="q-evo-title">
              <div>
                <h2 id="q-evo-title" className="ecoCard__title">Evolução da nota de uma loja</h2>
                <p className="ecoCard__subtitle">
                  {selectedStoreId && evolution
                    ? `${selectedStoreName ?? "Loja selecionada"} · média ${pct(evolution.mediaScore01)}`
                    : "Histórico auditoria por auditoria."}
                </p>
              </div>

              {!selectedStoreId || !evolution ? (
                <p className="ecoEmpty">Escolha uma loja no filtro acima para ver a evolução da nota dela.</p>
              ) : !evolution.items || evolution.items.length === 0 ? (
                <p className="ecoEmpty">Nenhuma auditoria encontrada.</p>
              ) : (
                <div className="ecoTableWrap">
                  <table className="ecoTable qDash__table qDash__table--inset">
                    <thead>
                      <tr>
                        <th scope="col">Data da auditoria</th>
                        <th scope="col">Nota de conformidade</th>
                        <th scope="col" className="ecoTable__num">Estrelas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {evolution.items.map((item: any, idx: number) => (
                        <tr key={idx}>
                          <td>{new Date(item.referenceDate).toLocaleDateString("pt-BR", { timeZone: "UTC" })}</td>
                          <td>
                            <span className="qDash__barCell">
                              <Bar value01={Number(item.score01)} band={scoreBand(Number(item.score01))} />
                              <strong>{pct(item.score01)}</strong>
                            </span>
                          </td>
                          <td className="ecoTable__num ecoTable__muted">{Number(item.stars).toFixed(1)} ★</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
